/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useMemo, useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Box } from "@mui/material";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import AdBlockComponent from "../Other/AdBlockComponent";
import PictureCard from "../Other/PictureCard";
import TierListOptions from "../Other/TierListOptions";
import AlbumLayout from "./AlbumLayout";
import { cloneDeep } from "lodash";

const GridTierList = (props) => {
  const theme = props.basicFunctions.getTheme();
  const { albumList, artistList, songList, listType, tierListName, customItems } = props.state;

  // The user's own custom-added items live alongside the site catalogue in each pool.
  const customAlbums = useMemo(() => customItems?.Albums || [], [customItems]);
  const customArtists = useMemo(() => customItems?.Artists || [], [customItems]);
  const customSongs = useMemo(() => customItems?.Songs || [], [customItems]);
  const [albums, setAlbums] = useState([]);
  const [artists, setArtists] = useState([]);
  const [songs, setSongs] = useState([]);
  const [haveNames, setHaveNames] = useState(true);
  const [gridSize, setGridSize] = useState(3);

  const covers = {
    albumCovers: {},
    profilePictures: {},
  };
  let coverType = "albumCovers";

  if (listType === "Albums" || listType === "Year") {
    coverType = "albumCovers";
    covers.albumCovers = useMemo(() => {
      const covers = {};
      if (albumList && Array.isArray(albumList)) {
        albumList.forEach((album) => {
          if (!covers[album.Id]) {
            covers[album.Id] = props.basicFunctions.getAlbumArt(album.Id);
          }
        });
      }

      return covers;
    }, [albumList, props.basicFunctions]);
  } else if (listType === "Artists") {
    coverType = "profilePictures";
    covers.profilePictures = useMemo(() => {
      const covers = {};
      artistList.forEach((artist) => {
        if (!covers[artist.Id]) {
          covers[artist.Id] = props.mainFunctions.getProfilePicture(
            artist.Artist,
            artist.ProfilePicture,
            artist.ProfilePicture
          );
        }
      });
      return covers;
    }, [artistList, props.mainFunctions]);
  } else if (listType === "Songs") {
    coverType = "albumCovers";
    covers.albumCovers = useMemo(() => {
      const covers = {};
      if (songList && Array.isArray(songList)) {
        songList.forEach((song) => {
          if (song.AlbumId && !covers[song.Id]) {
            covers[song.Id] = props.basicFunctions.getAlbumArt(song.AlbumId);
          }
        });
      }
      return covers;
    }, [songList, props.basicFunctions]);
  }

  useEffect(() => {
    if (albumList.length > 0 || customAlbums.length > 0) {
      setAlbums(cloneDeep([...customAlbums, ...albumList]));
    }
  }, [albumList, customAlbums]);

  useEffect(() => {
    if (artistList.length > 0 || customArtists.length > 0) {
      setArtists(cloneDeep([...customArtists, ...artistList]));
    }
  }, [artistList, customArtists]);

  useEffect(() => {
    if ((songList && songList.length > 0) || customSongs.length > 0) {
      setSongs(cloneDeep([...customSongs, ...(songList || [])]));
    }
  }, [songList, customSongs]);

  const createGrid = (size) => {
    const grid = {};
    const totalCells = size * size;
    for (let i = 1; i <= totalCells; i++) {
      grid[i.toString()] = null;
    }
    return grid;
  };

  const mergeItems = (currentItems, itemsToAdd) => {
    const existingIds = new Set(currentItems.map((item) => item.Id));
    const mergedItems = [...currentItems];

    itemsToAdd.forEach((item) => {
      if (item && !existingIds.has(item.Id)) {
        mergedItems.push(item);
      }
    });

    return mergedItems;
  };

  const [gridState, setGridState] = useState(() => createGrid(gridSize));

  const resetGrid = (size = gridSize) => {
    const gridItems = Object.values(gridState).filter(Boolean);

    if (listType === "Artists") {
      setArtists((prev) => mergeItems(prev, gridItems));
    } else if (listType === "Songs") {
      setSongs((prev) => mergeItems(prev, gridItems));
    } else {
      setAlbums((prev) => mergeItems(prev, gridItems));
    }

    setGridState(createGrid(size));
  };

  const handleGridSizeChange = (newSize) => {
    setGridSize(newSize);
    resetGrid(newSize);
  };

  const renderedGridItems = useMemo(
    () => (listType === "Albums" || listType === "Songs" ? Object.values(gridState).filter(Boolean) : []),
    [gridState, listType]
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        id="themeProviderBox"
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden scroll",
          height: "-webkit-fill-available",
        }}
      >
        <TierListOptions
          {...props}
          setHaveNames={setHaveNames}
          haveNames={haveNames}
          setArtists={setArtists}
          setSongs={setSongs}
          setAlbums={setAlbums}
          resetGrid={resetGrid}
          tierListTitle={tierListName}
          gridSize={gridSize}
          onGridSizeChange={handleGridSizeChange}
        />
        <Box
          id="tierBox"
          sx={{
            padding: 3,
            display: "-webkit-box",
            gap: 4,
            width: "100%",
          }}
        >
          <DragDropContext
            onDragEnd={(result) => {
              const dragProps = {
                result,
                albums,
                artists,
                songs,
                listType,
                gridState,
                setGridState,
                setAlbums,
                setArtists,
                setSongs,
              };
              props.tierListFunctions.onDragEndGrid(dragProps);
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "fit-content",
                }}
                id="tierList"
              >
                {tierListName !== "" && (
                  <Box
                    sx={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.5rem",
                    }}
                  >
                    {tierListName}
                  </Box>
                )}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
                    width: "fit-content",
                    padding: "10px",
                  }}
                >
                  {Object.keys(gridState).map((key) => {
                    const item = gridState[key];
                    return (
                      <Droppable
                        key={key}
                        droppableId={key}
                        ignoreContainerClipping={false}
                        isDropDisabled={false}
                        isCombineEnabled={false}
                      >
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              width: "200px",
                              height: "200px",
                              border: snapshot.isDraggingOver ? "2px solid var(--accent)" : "1px solid var(--border-soft)",
                              backgroundColor: snapshot.isDraggingOver ? "var(--accent-soft)" : "transparent",
                              transition: "background-color 120ms ease, border-color 120ms ease",
                            }}
                          >
                            {item && item.Id && (
                              <PictureCard
                                key={item.Id}
                                size={198}
                                imageSize={haveNames ? 150 : 198}
                                haveNames={haveNames}
                                keyItem={item.Id}
                                pictureItem={item}
                                cover={covers[coverType][item.Id]}
                                index={key}
                                draggableId={item.Id.toString()}
                                provided={provided}
                              />
                            )}
                            {provided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    );
                  })}
                </Box>
              </Box>
              <AdBlockComponent {...props} adSlot="3525069703" />
            </Box>

            {(albumList.length > 0 || artistList.length > 0 || (songList && songList.length > 0)) && (
              <Droppable
                droppableId="albumList"
                ignoreContainerClipping={false}
                isDropDisabled={false}
                isCombineEnabled={false}
              >
                {(provided) => (
                  <AlbumLayout
                    {...props}
                    provided={provided}
                    albums={albums}
                    artists={artists}
                    songs={songs}
                    coverType={coverType}
                    covers={covers}
                    renderedItems={renderedGridItems}
                    onShuffle={() => props.mainFunctions.shuffleAlbums()}
                  />
                )}
              </Droppable>
            )}
          </DragDropContext>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default GridTierList;
